import React, { useState } from 'react';
import styled from 'styled-components';

import { gaAlertAction } from '../lib/gaPixel';
import type { Alert } from '../hooks/useAlerts';

/**
 * Presentational alerts + sign-in-nudge surface.
 *
 * Extracted from HeaderAccountMenu so the SAME markup renders in three
 * hosts with no logic fork:
 *   - the desktop avatar dropdown (`variant="dropdown"`),
 *   - the mobile `/you` page notifications section (`variant="page"`),
 *   - the mobile board-page anon bell (`variant="dropdown"`).
 *
 * It owns NO data — callers pass `alerts` + `onDismiss` from `useAlerts`
 * and decide whether the sign-in nudge shows (`showNudge`, derived from
 * the anon + 3-saves threshold). Keeping it pure makes it Storybook- and
 * jsdom-safe (no Firebase init at import time).
 *
 * Ordering + rollup (Kate 2026-07-06 — "one big blob" feedback):
 *   - Time-sensitive `coming_up_soon` rows always render FIRST.
 *   - When ROLLUP_THRESHOLD+ `add_birthday` rows exist they collapse into
 *     one expandable row ("Add birthdays for Fred, Esme + 6 more") —
 *     every recipient generates one, so an un-grouped list is 90% the
 *     same chore repeated. Tapping expands in place; each expanded row
 *     is individually clickable/dismissable.
 *
 * `onSignIn`: when provided, the nudge banner renders its own "Sign in"
 * button. Hosts that already expose a separate sign-in affordance (the
 * avatar dropdown's account row; the You page's identity block) omit it.
 */

// Collapse same-type add_birthday rows at this many. Below it, individual
// rows are each directly actionable and grouping would just add a tap.
export const ROLLUP_THRESHOLD = 3;

/** Name from an "Add NAME's birthday" headline, for the rollup label.
 *  Null for name-less rows (headline "Add a birthday") — they count in
 *  the total but contribute no name. */
function birthdayName(a: Alert): string | null {
  const m = a.headline.match(/^Add (.+)'s birthday$/);
  return m ? m[1] : null;
}

export function rollupLabel(group: Alert[]): string {
  const names = group.map(birthdayName).filter((n): n is string => n !== null);
  // Fewer than 2 usable names (name-less recipients) → count-only label.
  if (names.length < 2) return `Add ${group.length} birthdays`;
  if (group.length === 2) return `Add birthdays for ${names[0]} and ${names[1]}`;
  const more = group.length - 2;
  if (more === 0) return `Add birthdays for ${names[0]} and ${names[1]}`;
  return `Add birthdays for ${names[0]}, ${names[1]} + ${more} more`;
}

interface AccountAlertsProps {
  alerts: Alert[];
  onAlertClick: (alert: Alert) => void;
  onDismiss: (alertId: string) => void;
  /** Show the "Sign in to keep your saved gift ideas" nudge banner above the list. */
  showNudge: boolean;
  /** When set, the nudge banner renders a Sign in button wired to this. */
  onSignIn?: () => void;
  /** Compact dropdown styling vs. roomier full-page styling. */
  variant?: 'dropdown' | 'page';
}

const Section = styled.div<{ $variant: 'dropdown' | 'page' }>`
  ${({ $variant, theme }) =>
    $variant === 'dropdown'
      ? `
        padding: 4px 0 6px;
        border-bottom: 1px solid ${theme.color.warmBorder};
        margin-bottom: 8px;
      `
      : `
        padding: 0;
      `}
`;

// "Sign in to keep your saved gift ideas" banner. Headline pairs an inline red dot
// with the copy so the user connects "the red dot I saw" with "this is
// what it points at." Same red as the avatar/You-tab ThresholdDot.
const NudgeAlert = styled.div<{ $variant: 'dropdown' | 'page' }>`
  padding: 12px;
  margin-bottom: 8px;
  border-radius: ${({ $variant }) => ($variant === 'page' ? '12px' : '8px')};
  background: ${({ theme }) => theme.color.creamLight};
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13px;
  line-height: 1.45;
  color: hsl(var(--foreground));
  .headline {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    font-weight: 600;
  }
  .inline-dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: #d93025;
  }
`;

const NudgeSignIn = styled.button`
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 16px;
  border-radius: 9999px;
  border: 0;
  background: ${({ theme }) => theme.color.clay};
  color: #ffffff;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 150ms ease;
  &:hover {
    opacity: 0.9;
  }
`;

// One alert row. The whole row is the click-target (navigates to the
// alert's href); the × on the right dismisses without navigating.
//
// `$today` = day-of celebration state for coming_up_soon rows: a clay
// gradient from the brand --primary token (same family as the
// ComingUpStrip day-of card, so the two surfaces rhyme). Celebration,
// not just another reminder. (Gold --primary-glow tried first — Kate
// 2026-07-10: looked off; clay sits in-family.)
const AlertRow = styled.div<{ $today?: boolean }>`
  display: grid;
  grid-template-columns: 24px 1fr 22px;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 150ms ease;
  ${({ $today }) =>
    $today
      ? `background: linear-gradient(120deg,
           hsl(var(--primary) / 0.26) 0%,
           hsl(var(--primary) / 0.10) 55%,
           transparent 100%);`
      : ''}
  &:hover {
    background: ${({ $today, theme }) =>
      $today
        ? `linear-gradient(120deg,
             hsl(var(--primary) / 0.34) 0%,
             hsl(var(--primary) / 0.14) 55%,
             transparent 100%)`
        : theme.color.creamLight};
  }
  + .alert-row {
    margin-top: 2px;
  }
`;

const AlertEmoji = styled.span`
  font-size: 18px;
  line-height: 1;
  text-align: center;
`;

const AlertHeadline = styled.span`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--foreground));
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AlertDismiss = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  border: none;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  &:hover {
    background: ${({ theme }) => theme.color.cream};
    color: hsl(var(--foreground));
  }
`;

// Chevron on the rollup row — sits in the grid slot the × occupies on
// normal rows, rotating when expanded.
const RollupChevron = styled.span<{ $expanded: boolean }>`
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--muted-foreground));
  font-size: 13px;
  transition: transform 150ms ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

export const AccountAlerts: React.FC<AccountAlertsProps> = ({
  alerts,
  onAlertClick,
  onDismiss,
  showNudge,
  onSignIn,
  variant = 'dropdown',
}) => {
  // Expand-in-place state for the birthday rollup. Resets on remount
  // (dropdown close/reopen, page nav) — collapsed is the default view.
  const [rollupExpanded, setRollupExpanded] = useState(false);

  // Render nothing when there's neither a nudge nor any alerts — keeps
  // hosts from drawing an empty bordered section.
  if (!showNudge && alerts.length === 0) return null;

  const handleDismiss = (e: React.MouseEvent, alertId: string) => {
    // Stop the row's click handler from also firing — otherwise dismiss
    // would navigate the user away mid-action.
    e.stopPropagation();
    onDismiss(alertId);
  };

  const renderRow = (a: Alert) => (
    <AlertRow
      key={a.alertId}
      className="alert-row"
      role="menuitem"
      tabIndex={0}
      $today={a.isToday}
      data-today={a.isToday || undefined}
      onClick={() => {
        gaAlertAction({ action: 'click', alert_type: a.type });
        onAlertClick(a);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          gaAlertAction({ action: 'click', alert_type: a.type });
          onAlertClick(a);
        }
      }}
    >
      <AlertEmoji aria-hidden="true">{a.emoji || '🔔'}</AlertEmoji>
      <AlertHeadline>{a.headline}</AlertHeadline>
      <AlertDismiss
        type="button"
        aria-label={`Dismiss ${a.headline}`}
        onClick={(e) => {
          gaAlertAction({ action: 'dismiss', alert_type: a.type });
          handleDismiss(e, a.alertId);
        }}
      >
        ×
      </AlertDismiss>
    </AlertRow>
  );

  // Time-sensitive rows first; birthday chores after (rolled up at
  // ROLLUP_THRESHOLD). Everything else keeps its incoming order.
  // isPast rows are HIDDEN, not styled: the BE retains a day-of alert
  // one extra day so every timezone gets its full local day, and the
  // FE is the layer that knows the viewer's calendar has moved on.
  const timeSensitive = alerts.filter(
    (a) => a.type === 'coming_up_soon' && !a.isPast,
  );
  const birthdayChores = alerts.filter((a) => a.type === 'add_birthday');
  const rest = alerts.filter(
    (a) => a.type !== 'coming_up_soon' && a.type !== 'add_birthday',
  );
  const rollup = birthdayChores.length >= ROLLUP_THRESHOLD;

  return (
    <Section $variant={variant} data-testid="account-alerts">
      {showNudge && (
        <NudgeAlert $variant={variant} role="status" data-testid="threshold-nudge">
          <span className="headline">
            <span className="inline-dot" aria-hidden />
            <span>Sign in to keep your saved gift ideas</span>
          </span>
          Your saved gift ideas will be here when you come back.
          {onSignIn && (
            <div>
              <NudgeSignIn type="button" onClick={onSignIn}>
                Sign in
              </NudgeSignIn>
            </div>
          )}
        </NudgeAlert>
      )}
      {alerts.length > 0 && (
        <div data-testid="alerts-section">
          {timeSensitive.map(renderRow)}
          {rest.map(renderRow)}
          {rollup ? (
            <>
              <AlertRow
                className="alert-row"
                role="menuitem"
                tabIndex={0}
                aria-expanded={rollupExpanded}
                data-testid="birthday-rollup"
                onClick={() => {
                  gaAlertAction({ action: 'expand', alert_type: 'add_birthday' });
                  setRollupExpanded((v) => !v);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    gaAlertAction({ action: 'expand', alert_type: 'add_birthday' });
                    setRollupExpanded((v) => !v);
                  }
                }}
              >
                <AlertEmoji aria-hidden="true">🎂</AlertEmoji>
                <AlertHeadline>{rollupLabel(birthdayChores)}</AlertHeadline>
                <RollupChevron aria-hidden="true" $expanded={rollupExpanded}>
                  ›
                </RollupChevron>
              </AlertRow>
              {rollupExpanded && birthdayChores.map(renderRow)}
            </>
          ) : (
            birthdayChores.map(renderRow)
          )}
        </div>
      )}
    </Section>
  );
};

export default AccountAlerts;
